/**
 * 新しい案件（039、docs/28 §4 → #96 作成画面の刷新、docs/36 §3）。/groups/new（?client= で顧客が入った状態で開く）
 *   上に藍の帯（‹ 戻り先・題・段）、左に段のカード（1 {customer} → 2 {project} → 3 支払と請求）、右に「できあがりの{project}」のカード。
 *   段1：探す欄＋よく使う{customer}のタイル → 選んだら 支店・{customer}担当者（その場で登録も）
 *   段2：{project}名（必須）・自社の担当者・現場住所・「施主名入力」・社内メモ（#97 で段3から移した）
 *   段3「支払と請求」（#97 で「お金の決まり」から）：支払条件（押せるカード：既定の条件／この{project}だけ変える）・請求タイミング（一括／分割）
 *   作成画面の欄は Chrome の住所・氏名の自動入力を出さない（#97）：autoComplete="off"、id は住所・氏名と思われない名前（gn_case・gn_spot・gn_orderer）
 *   #289（docs/101 §7）：使う機能「{ongoingProject}」がオンの会社だけ 段2 に「継続」のスイッチと開始日（既定 今日）。
 *     オンにすると{project}名の既定は{customer}の略称＋{ongoingProject}（打ち直した名前は変えない）
 *   必須は{customer}と{project}名だけ（ルール6.3）。そろうまで「{project}を作る」は押せない。作ったら{project}の画面へ
 * 9/29（#92、docs/35）：欄の添え書き・下の説明の文は無くし、見出しの「？」と請求タイミングの「？」にまとめた。担当者の欄の名前は「自社の担当者」（決め10）
 */
import { BILLING_TIMING_LABEL, RECURRING_MSG, DEFAULT_PAYMENT_TERMS, DEFAULT_PRODUCT, PRODUCTS, type BillingTiming, type EstimateSettings, GROUP_MSG, installmentDefaultNames, ongoingNameOf, paymentTermsLabel, displayNameOf, ONGOING_MSG, SCHEDULE_MSG, type GroupCreate, type GroupDetail, type Partner, type PaymentTerms } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { useMe } from "../auth.jsx";
import { hasModule } from "../core/slots.jsx";
import { ESTIMATE_SETTINGS_KEY } from "./SettingsEstimate.jsx";
import { Choice, CreateActions, CreateBand, CrIcon, PreviewAside, PreviewMini, PreviewSheet, StepCard, stepStates, todayJp } from "../components/Create.jsx";
import { PartnerChip, PartnerField, PartnerPickerDrawer, type PickedPartner } from "../components/PartnerPickerDrawer.jsx";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { BranchSelect, ContactSelect, TermsChooser, defaultsOf, partyFieldsHelp, termsHelp } from "../components/GroupFields.jsx";
import { ErrorText, Field, Help, Switch, guardComposingEnter } from "../components/ui.jsx";
import { DateInput } from "../components/DateInput.jsx";
import { useOngoing } from "../lib/ongoing.js";
import { todayJst } from "../lib/num.js";
import { t, useLabels } from "../labels.js";

type Form = {
  branchId: string | null;
  contactId: string | null;
  name: string;
  staff: string;
  terms: PaymentTerms | null;
  memo: string;
  site: string;
  owner: string;
  /** 「施主名入力」を押したか（値があれば出したまま） */
  ownerOpen: boolean;
  timing: BillingTiming;
  /** 継続案件（#289）。オンの会社だけ出す */
  ongoing: boolean;
  startedOn: string;
};

/** カードの上で光らせる所 */
type Part = "name" | "client" | "staff";

export function GroupNewPage() {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  /** 施主名はモジュール field の会社だけ（#142 段2、docs/52 §2） */
  const field = hasModule(me.data, "field");
  /** 請求タイミング「定期」（#268）は sales の会社だけ */
  const timings: BillingTiming[] = hasModule(me.data, "sales") ? ["lump", "split", "recurring"] : ["lump", "split"];
  const og = useOngoing();
  /** {project}名を人が打ったか（打っていなければ 継続のオン・オフ・{customer}の入れ替えで既定の名前を入れ直す） */
  const nameTouched = useRef(false);
  /** 会社の分割の既定（設定 → 見積・請求。#142 段3）。無ければ製品の既定 */
  const estSettings = useQuery({ queryKey: ESTIMATE_SETTINGS_KEY, queryFn: () => api<EstimateSettings>("GET", "/company/estimate-settings"), staleTime: 60_000 });
  const instDefaults = estSettings.data?.installments ?? null;
  const [sp] = useSearchParams();
  const entryClient = useRef(sp.get("client"));
  const [clientId, setClientId] = useState<string | null>(sp.get("client"));
  const [partner, setPartner] = useState<Partner | null>(null);
  // 案件なしの{schedule}から「新しい{project}を作成」（#291）：?scheduleId=&name=&client=。保存したら紐づけて{schedule}へ戻る
  const attachTo = useRef(sp.get("scheduleId"));
  if (sp.get("name")) nameTouched.current = true;
  const [f, setF] = useState<Form>({ branchId: null, contactId: null, name: (sp.get("name") ?? "").slice(0, 100), staff: "", terms: null, memo: "", site: "", owner: "", ownerOpen: false, timing: "lump", ongoing: false, startedOn: todayJst() });
  const [err, setErr] = useState<{ client?: string | undefined; name?: string | undefined; other?: string | undefined }>({});
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState<Part | null>(null);
  const [sheet, setSheet] = useState(false);
  /** 画面の中で{customer}を選んだか（入れかけの確かめに使う。?client= で入った分は数えない） */
  const [picked, setPicked] = useState(false);
  /** ドロアーで支店・担当者まで選んだ（{customer}の詳細が届いたら既定の代わりに入れる。#288） */
  const pickedParty = useRef<{ branchId: string | null; contactId: string | null } | null>(null);
  const [drawer, setDrawer] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const ownerRef = useRef<HTMLInputElement>(null);
  const staffTouched = useRef(false);
  const set = (p: Partial<Form>) => setF((x) => ({ ...x, ...p }));

  // 担当者の既定はログインしている人の氏名（決めごと14）。打ち直したら上書きしない
  const myName = me.data?.user.name ?? "";
  useEffect(() => {
    if (!staffTouched.current && myName) setF((x) => (x.staff ? x : { ...x, staff: myName }));
  }, [myName]);

  // 顧客の詳細（支店・担当者・支払条件）。選んだ瞬間に引く
  const pq = useQuery({ queryKey: ["partner", clientId], queryFn: () => api<Partner>("GET", `/partners/${clientId}`), enabled: !!clientId });
  const cq = useQuery({ queryKey: ["partner", entryClient.current], queryFn: () => api<Partner>("GET", `/partners/${entryClient.current}`), enabled: !!entryClient.current });
  useEffect(() => {
    if (!pq.data || pq.data.id === partner?.id) return;
    setPartner(pq.data);
    const d = defaultsOf(pq.data);
    const party = pickedParty.current;
    set({ ...d, ...(party ? { branchId: party.branchId ?? (party.contactId ? null : d.branchId), contactId: party.contactId ?? (party.branchId ? null : d.contactId) } : {}), terms: null });
    setTimeout(() => nameRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pq.data]);

  /** ドロアーで選んだ（#288）：{customer}を入れ替え、支店・担当者も選んでいればその値で */
  const onPicked = (c: PickedPartner) => {
    pickedParty.current = c.branch || c.contact ? { branchId: c.branch?.id ?? null, contactId: c.contact?.id ?? null } : null;
    setErr({});
    setPicked(true);
    if (c.id === clientId && partner) {
      if (pickedParty.current) set({ branchId: pickedParty.current.branchId, contactId: pickedParty.current.contactId });
      return;
    }
    setPartner(null);
    set({ branchId: null, contactId: null, terms: null });
    setClientId(c.id);
  };

  // 継続のオン・{customer}の入れ替え：打っていない名前は 略称＋{ongoingProject}（オフに戻すと空）
  const ongoingName = partner ? ongoingNameOf(displayNameOf(partner), og.word) : "";
  useEffect(() => {
    if (nameTouched.current) return;
    setF((x) => ({ ...x, name: x.ongoing ? ongoingName : "" }));
  }, [f.ongoing, ongoingName]);

  const name = f.name.trim();
  const ready = !!partner && !!name;
  const create = async () => {
    const e: typeof err = {};
    if (!clientId) e.client = t(GROUP_MSG.clientRequired);
    if (clientId && !name) e.name = t(GROUP_MSG.nameRequired);
    if (e.client || e.name) {
      setErr(e);
      (e.client ? document.querySelector<HTMLInputElement>(".crsbox input") : nameRef.current)?.focus();
      return;
    }
    setBusy(true);
    setErr({});
    try {
      const body: GroupCreate = {
        clientId: clientId!,
        name,
        partnerBranchId: f.branchId,
        partnerContactId: f.contactId,
        staffName: f.staff.trim(),
        paymentTerms: f.terms,
        memo: f.memo.trim(),
        siteAddress: f.site.trim(),
        ownerName: f.owner.trim(),
        billingTiming: f.timing,
        ...(og.on && f.ongoing ? { isOngoing: true, startedOn: f.startedOn || null } : {}),
      };
      const g = await api<GroupDetail>("POST", "/groups", body);
      void qc.invalidateQueries({ queryKey: ["group-list"] });
      void qc.invalidateQueries({ queryKey: ["groups", g.client.id] });
      void qc.invalidateQueries({ queryKey: ["partners", "used"] });
      if (attachTo.current) {
        // 案件なしの{schedule}を紐づけて{schedule}へ戻る（#291）
        await api("POST", `/schedules/${attachTo.current}/attach`, { groupId: g.id });
        void qc.invalidateQueries({ queryKey: ["schedule-overview"] });
        nav(`/groups/${g.id}/schedule`, { replace: true, state: { notice: t(SCHEDULE_MSG.attachedToNew) } });
        return;
      }
      nav(`/groups/${g.id}`, { replace: true, state: { notice: t(GROUP_MSG.created).replace("{name}", g.name) } });
    } catch (x) {
      const fe = x instanceof ApiError ? x.problem.errors : undefined;
      if (fe?.name) {
        setErr({ name: String(fe.name) });
        nameRef.current?.focus();
      } else if (fe?.clientId) setErr({ client: String(fe.clientId) });
      else setErr({ other: messageOf(x) });
      setBusy(false);
    }
  };

  const clientTerms = (partner?.paymentTerms ?? null) as PaymentTerms | null;
  /** 段2の「？」（#92）：欄の説明をまとめる */
  const help = `${L.project}名は最初の見積の${L.subject}になります（あとで修正できます。紙には出ません）。自社の担当者は、見積の「担当」に写ります。${partyFieldsHelp(L)}${termsHelp(L)}社内メモは暗号化して保存し、紙には出ません。${L.siteAddress}は見積の${L.site}に写ります。${field ? "施主名は下請けのときに入れます（暗号化して保存します）。" : ""}`;

  // 段（docs/36 §1）：1 は{customer}を選んだら済。2 は{project}名が入ったら済。3 は主役のボタンで終える段
  const steps = [
    { id: "gn-step-client", label: L.customer },
    { id: "gn-step-group", label: L.project },
    { id: "gn-step-pay", label: "支払と請求" },
  ];
  const states = stepStates([!!partner, ready, false]);
  const open = !!partner;
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ ?client= の{customer}・{project}の一覧。入れかけなら確かめる
  const dirty = picked || !!f.name.trim() || !!f.site.trim() || !!f.owner.trim() || !!f.memo.trim() || f.terms !== null || f.timing !== "lump" || (staffTouched.current && f.staff.trim() !== myName);
  const back = useBackNav(entryClient.current ? { to: `/partners/${entryClient.current}`, label: cq.data?.name ?? L.customer } : { to: "/groups", label: L.project }, dirty);
  usePageName(`新しい${L.project}`);

  const contact = partner?.contacts.find((c) => c.id === f.contactId) ?? null;
  const branch = partner?.branches.find((b) => b.id === f.branchId) ?? null;
  const hl: Part | null = focus ?? (!partner ? "client" : !name ? "name" : null);
  const card = (
    <GroupCard
      name={name}
      client={partner ? `${partner.name}${branch ? `　${branch.name}` : ""}` : ""}
      contact={contact?.name ?? ""}
      staff={f.staff.trim()}
      terms={paymentTermsLabel(f.terms ?? clientTerms ?? DEFAULT_PAYMENT_TERMS)}
      timing={f.timing}
      hl={hl}
    />
  );

  return (
    <div className="crpage" data-testid="group-new">
      <div className="crin">
        <CreateBand back={back} title={`新しい${L.project}`} steps={steps} states={states} deco="building" />
        <div className="crgrid">
          <div className="crmain">
            <PreviewMini
              thumb={<CrIcon k="folder" />}
              line1={!partner ? `できあがりの${L.project}` : name || partner.name}
              line2={partner && name && name !== partner.name ? partner.name : ""}
              open={sheet}
              onToggle={() => setSheet((v) => !v)}
              buttonLabel={`${L.project}表示`}
              sheetId="gn-sheet"
            />
            <StepCard
              id={steps[0]!.id}
              n={1}
              title={L.customer}
              state={states[0]!}
              open
            >
              {clientId ? (
                <>
                  <PartnerChip label={L.customer} name={partner?.displayName ?? partner?.name ?? "…"} branch={branch?.name ?? null} contact={contact?.name ?? null} onChange={() => setDrawer(true)} />
                  {partner ? (
                    <div className="crtwo">
                      <BranchSelect float partner={partner} id="gn_branch" value={f.branchId} onChange={(v) => set({ branchId: v })} onFocus={() => setFocus("client")} onBlur={() => setFocus(null)} />
                      <ContactSelect float partner={partner} branchId={f.branchId} id="gn_contact" value={f.contactId} onChange={(v) => set({ contactId: v })} onPartner={setPartner} onFocus={() => setFocus("client")} onBlur={() => setFocus(null)} />
                    </div>
                  ) : (
                    <p className="hint" style={{ margin: 0 }}>読み込んでいます…</p>
                  )}
                </>
              ) : (
                <PartnerField role="customer" label={L.customer} value={null} autoFocus onPick={onPicked} testId="client-field" />
              )}
              <PartnerPickerDrawer open={drawer} role="customer" onClose={() => setDrawer(false)} onPick={onPicked} />
              <ErrorText>{err.client ?? (pq.isError ? messageOf(pq.error) : undefined)}</ErrorText>
            </StepCard>
            <StepCard id={steps[1]!.id} n={2} title={L.project} state={states[1]!} open={open} help={<Help testId="help-group-new" label={`新しい${L.project}の説明`} text={help} />}>
              <div>
                <Field
                  id="gn_case"
                  className="big"
                  label={
                    <>
                      {L.project}名<span className="req">必須</span>
                    </>
                  }
                >
                  <input ref={nameRef} id="gn_case" className="inp" autoComplete="off" value={f.name} onChange={(e) => { nameTouched.current = true; set({ name: e.target.value }); }} onKeyDown={guardComposingEnter} onFocus={() => setFocus("name")} onBlur={() => setFocus(null)} />
                </Field>
                <ErrorText>{err.name}</ErrorText>
              </div>
              {og.on ? (
                <div className="crtwo" data-testid="gn-ongoing-row">
                  <div className="fld" style={{ justifyContent: "center" }}>
                    <Switch checked={f.ongoing} onChange={(v) => set({ ongoing: v })} label={og.short} testId="gn-ongoing" />
                  </div>
                  {f.ongoing ? (
                    <Field id="gn_started" label={ONGOING_MSG.startedOn}>
                      <DateInput id="gn_started" label={ONGOING_MSG.startedOn} value={f.startedOn} onChange={(v) => set({ startedOn: v })} testId="gn-started" />
                    </Field>
                  ) : null}
                </div>
              ) : null}
              <div className="crtwo">
                <Field id="gn_staff" label="自社の担当者">
                  <input
                    id="gn_staff"
                    className="inp"
                    autoComplete="off"
                    value={f.staff}
                    onChange={(e) => {
                      staffTouched.current = true;
                      set({ staff: e.target.value });
                    }}
                    onFocus={() => setFocus("staff")}
                    onBlur={() => setFocus(null)}
                  />
                </Field>
                <Field id="gn_spot" label={L.siteAddress}>
                  <input id="gn_spot" className="inp" autoComplete="off" value={f.site} onChange={(e) => set({ site: e.target.value })} />
                </Field>
              </div>
              {/* 施主名はモジュール field の会社だけ（#142 段2） */}
              {!field ? null : f.ownerOpen || f.owner ? (
                <Field id="gn_orderer" label="施主名">
                  <input ref={ownerRef} id="gn_orderer" className="inp" autoComplete="off" value={f.owner} onChange={(e) => set({ owner: e.target.value })} />
                </Field>
              ) : (
                <div>
                  <button
                    type="button"
                    className="btn text"
                    onClick={() => {
                      set({ ownerOpen: true });
                      setTimeout(() => ownerRef.current?.focus(), 0);
                    }}
                    data-testid="gn-owner-open"
                  >
                    施主名入力
                  </button>
                </div>
              )}
              <Field id="gn_memo" label="社内メモ">
                <textarea id="gn_memo" className="inp" autoComplete="off" value={f.memo} onChange={(e) => set({ memo: e.target.value })} />
              </Field>
            </StepCard>
            <StepCard id={steps[2]!.id} n={3} title="支払と請求" state={states[2]!} open={open}>
              <TermsChooser variant="cards" clientTerms={clientTerms} value={f.terms} onChange={(v) => set({ terms: v })} idBase="gn_terms" />
              <div className="fld">
                <span className="crsub" id="gn_timing_lbl">
                  請求タイミング
                  <Help testId="help-timing" text={`あとで変えられます。一括＝受注した見積1枚につき請求書1枚。分割にすると、受注した見積ごとに「分割予定」（${installmentDefaultNames(me.data?.tenant.product, instDefaults)}）を作れます。${timings.includes("recurring") ? RECURRING_MSG.timingHelp : ""}`} />
                </span>
                <div className="crchoices" role="radiogroup" aria-labelledby="gn_timing_lbl">
                  {timings.map((k) => (
                    <Choice key={k} stack checked={f.timing === k} onClick={() => set({ timing: k })} title={BILLING_TIMING_LABEL[k]} testId={`gn-timing-${k}`}>
                      <span className={"crbars" + (k !== "lump" ? " three" : "")} aria-hidden="true">
                        <i />
                        {k !== "lump" ? (
                          <>
                            <i />
                            <i />
                          </>
                        ) : null}
                      </span>
                      <span className={"crcap" + (k === "split" ? " three" : "")}>
                        {k === "recurring" ? (
                          RECURRING_MSG.timingCaption
                        ) : k === "split" ? (
                          <>
                            {/* 会社の分割の既定（設定 → 見積・請求）があればその名前、無ければ製品の短い名前（#142 段1・段3） */}
                            {(instDefaults ? instDefaults.map((x) => x.label) : PRODUCTS[me.data?.tenant.product ?? DEFAULT_PRODUCT].installments.map((x) => x.short)).map((x, i) => (
                              <span key={i}>{x}</span>
                            ))}
                          </>
                        ) : (
                          "1回で請求"
                        )}
                      </span>
                    </Choice>
                  ))}
                </div>
              </div>
            </StepCard>
            <ErrorText>{err.other}</ErrorText>
            <CreateActions back={back} label={`${L.project}作成`} disabled={!ready || busy} onClick={() => void create()} testId="group-create" />
          </div>
          <PreviewAside title={`できあがりの${L.project}`} icon="folder">
            {card}
          </PreviewAside>
        </div>
      </div>
      {back.dialog}
      {createPortal(
        <PreviewSheet id="gn-sheet" open={sheet} onClose={() => setSheet(false)} title={`できあがりの${L.project}`}>
          {card}
        </PreviewSheet>,
        document.body,
      )}
    </div>
  );
}

/**
 * 右の{project}のカード（docs/36 §3）：しるし「見積なし」・今日の日付／{project}名／{customer}名＋支店・{customer}担当者・自社の担当者・支払条件・請求は一括／分割／
 * 「この先」：{project}を作る（✓）→ 見積を作る（次はここ）→ 受注 → 請求 → 入金
 */
function GroupCard({ name, client, contact, staff, terms, timing, hl }: { name: string; client: string; contact: string; staff: string; terms: string; timing: BillingTiming; hl: Part | null }) {
  const L = useLabels();
  const part = (p: Part) => ({ "data-part": p, "data-hl": hl === p ? "" : undefined });
  const flow: [string, "done" | "next" | "todo"][] = [
    [`${L.project}を作る`, "done"],
    ["見積を作る", "next"],
    ["受注", "todo"],
    ["請求", "todo"],
    ["入金", "todo"],
  ];
  return (
    <div className="gpv" data-testid="group-card">
      <div className="gpvtop">
        <span className="chip quiet">見積なし</span>
        <span className="sp" />
        <span className="muted">{todayJp(false)}</span>
      </div>
      <div className="gpvname" {...part("name")} data-testid="card-name">
        {name || <span className="gbar w70" />}
      </div>
      <ul className="gpvrows">
        <li {...part("client")} data-testid="card-client">
          <CrIcon k="building" />
          {client || <span className="gbar w60" />}
        </li>
        {contact ? (
          <li data-testid="card-contact">
            <CrIcon k="user" />
            {contact}
          </li>
        ) : null}
        <li {...part("staff")} data-testid="card-staff">
          <span className="crav sm">{staff ? Array.from(staff)[0] : ""}</span>
          {staff || <span className="gbar w40" />}
        </li>
        <li data-testid="card-terms">
          <CrIcon k="yen" />
          {terms}
        </li>
        <li data-testid="card-timing">
          <CrIcon k="cal" />
          請求は{BILLING_TIMING_LABEL[timing]}
        </li>
      </ul>
      <div className="gpvflowh">この先</div>
      <ol className="gpvflow">
        {flow.map(([label, st]) => (
          <li key={label} data-state={st}>
            <span className="dot">{st === "done" ? <CrIcon k="check" /> : null}</span>
            <span>{label}</span>
            {st === "next" ? <span className="chip ai">次はここ</span> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}
